import * as Input from "../../components/Form/Input";
import { Calendar, Image, Pencil, MapPin } from "lucide-react";
import { Button } from "../../components/Button";
import { ChangeEvent, useState, useEffect } from "react";
import { EventGalleryInput } from "../../components/Form/EventGalleryInput";
import { EventInstitutionsPicker } from "../../components/Form/EventInstitutionsPicker";
import Map from "../../components/map";

import { Editor } from "primereact/editor";
import api from "../../service/api";
import { useLoading } from "../../store/useLoading";
import { Loading } from "../../components/Loading";
import { useTabContext } from ".";
import { toastError, toastInfo, toastSuccess } from "../../utils/toast";

export function CreateEvent() {
  const { setCurrentTab } = useTabContext();
  const loading = useLoading((store) => store.isLoading);
  const setLoading = useLoading((store) => store.setLoading);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [dateInit, setDateInit] = useState("");
  const [dateEnd, setDateEnd] = useState("");
  const [banner, setBanner] = useState<File>({} as File);
  const [banner_site, setBannerSite] = useState<File>({} as File);
  const [galleryPhotos, setGalleryPhotos] = useState<File[]>([]);
  const [latitude, setLatitude] = useState<number | null>(null);
  const [longitude, setLongitude] = useState<number | null>(null);
  const [mapCenter, setMapCenter] = useState<[number, number]>([-23.5505, -46.6333]);
  const [allowAllInstitutions, setAllowAllInstitutions] = useState(true);
  const [institutionIds, setInstitutionIds] = useState<string[]>([]);

  // Handle gallery photos changes
  const handleGalleryPhotosChange = (photos: File[]) => {
    setGalleryPhotos(photos);
  };

  // Atualizar centro do mapa quando coordenadas mudarem
  useEffect(() => {
    if (latitude && longitude) {
      setMapCenter([latitude, longitude]);
    }
  }, [latitude, longitude]);

  // Handler para quando clicar no mapa
  const handleMapClick = (position: [number, number]) => {
    setLatitude(position[0]);
    setLongitude(position[1]);
    setMapCenter(position);
  };

  function handleBannerSelected(event: ChangeEvent<HTMLInputElement>) {
    if (!event.target.files) {
      return;
    }
    setBanner(event.target.files[0]);
  }

  function handleBannerSiteSelected(event: ChangeEvent<HTMLInputElement>) {
    if (!event.target.files) {
      return;
    }
    setBannerSite(event.target.files[0]);
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    if (!title || !description || !dateInit || !dateEnd || !banner) {
      setLoading(false);
      return toastInfo("Preencha todos os campos");
    }

    if (!allowAllInstitutions && institutionIds.length === 0) {
      setLoading(false);
      return toastInfo("Selecione ao menos uma instituição ou marque “todas”.");
    }

    const formData = new FormData();

    formData.append("banner", banner);
    formData.append("banner_site", banner_site);
    formData.append("name", title);
    formData.append("description", description);
    formData.append("initial_date", dateInit);
    formData.append("finish_date", dateEnd);
    if (latitude !== null) {
      formData.append("latitude", latitude.toString());
    }
    if (longitude !== null) {
      formData.append("longitude", longitude.toString());
    }

    const headers = {
      headers: { "Content-Type": "multipart/form-data" },
    };

    try {
      // Criar evento
      const eventResponse = await api.post("events", formData, headers);
      const createdId = eventResponse.data?.id as string | undefined;

      if (createdId) {
        await api.put("/events/institutions", {
          event_id: createdId,
          allow_all_institutions: allowAllInstitutions,
          user_ids: allowAllInstitutions ? [] : institutionIds,
        });
      }

      // Se há fotos na galeria, fazer upload delas
      if (galleryPhotos.length > 0 && createdId) {
        const galleryFormData = new FormData();
        galleryFormData.append("event_id", createdId);

        galleryPhotos.forEach((photo) => {
          galleryFormData.append("photos", photo);
        });

        await api.post("events/add-photos", galleryFormData, headers);
      }

      toastSuccess("Evento cadastrado com sucesso!");
      setCurrentTab("tab1");
    } catch (err) {
      return toastError("Erro ao cadastrar evento");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="mt-6 flex flex-col">
        <div className="flex flex-col justify-between gap-4 lg:flex-row lg:items-center">
          <div className="flex flex-col gap-1">
            <h2 className="text-lg font-medium text-zinc-900 dark:text-white">
              Cadastrar evento
            </h2>
            <span className="text-sm text-zinc-500 dark:text-zinc-400">
              Preencha os dados do novo evento.
            </span>
          </div>
        </div>
      </div>

      {loading && <Loading />}
      {!loading && (
        <form
          id="users"
          className="mt-6 flex w-full flex-col gap-5 divide-y divide-zinc-200 dark:divide-zinc-800"
          onSubmit={handleSubmit}
        >
          <div className="grid gap-3 lg:grid-cols-form">
            <label
              htmlFor="event_title"
              className="text-sm font-medium text-zinc-700 dark:text-zinc-100"
            >
              Título do evento
            </label>
            <div className="flex gap-3">
              <Input.Root>
                <Input.Prefix>
                  <Pencil className="h-5 w-5 text-zinc-500" />
                </Input.Prefix>
                <Input.Control
                  name="event_title"
                  placeholder="Informe o título do evento"
                  id="event_title"
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </Input.Root>
            </div>
          </div>
          <div className="grid gap-3 lg:grid-cols-form pt-5">
            <label
              htmlFor="description"
              className="text-sm font-medium text-zinc-700 dark:text-zinc-100"
            >
              Descrição
            </label>
            <div className="flex gap-3 rounded-lg overflow-hidden">
              <Editor
                className="w-full"
                value={description}
                onTextChange={(e) => setDescription(e.htmlValue ?? "")}
                style={{ height: "320px" }}
              />
            </div>
          </div>

          <div className="grid gap-3 lg:grid-cols-form pt-5">
            <label
              htmlFor="initial_date"
              className="text-sm font-medium text-zinc-700 dark:text-zinc-100"
            >
              Data de início
            </label>
            <div className="flex gap-3">
              <Input.Root>
                <Input.Prefix>
                  <Calendar className="h-5 w-5 text-zinc-500" />
                </Input.Prefix>
                <Input.Control
                  id="initial_date"
                  type="date"
                  name="initial_date"
                  value={dateInit}
                  onChange={(e) => setDateInit(e.target.value)}
                />
              </Input.Root>
            </div>
          </div>

          <div className="grid gap-3 lg:grid-cols-form pt-5">
            <label
              htmlFor="finish_date"
              className="text-sm font-medium text-zinc-700 dark:text-zinc-100"
            >
              Data final
            </label>
            <div className="flex gap-3">
              <Input.Root>
                <Input.Prefix>
                  <Calendar className="h-5 w-5 text-zinc-500" />
                </Input.Prefix>
                <Input.Control
                  id="finish_date"
                  type="date"
                  name="finish_date"
                  value={dateEnd}
                  onChange={(e) => setDateEnd(e.target.value)}
                />
              </Input.Root>
            </div>
          </div>

          <div className="grid gap-3 lg:grid-cols-form pt-5">
            <label
              htmlFor="location"
              className="text-sm font-medium text-zinc-700 dark:text-zinc-100"
            >
              Localização
            </label>
            <div className="flex flex-col gap-4">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Input.Root>
                  <Input.Prefix>
                    <MapPin className="h-5 w-5 text-zinc-500" />
                  </Input.Prefix>
                  <Input.Control
                    name="latitude"
                    placeholder="Latitude (ex: -23.5505)"
                    id="latitude"
                    type="number"
                    step="any"
                    value={latitude ?? ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      setLatitude(value ? parseFloat(value) : null);
                    }}
                  />
                </Input.Root>
                <Input.Root>
                  <Input.Prefix>
                    <MapPin className="h-5 w-5 text-zinc-500" />
                  </Input.Prefix>
                  <Input.Control
                    name="longitude"
                    placeholder="Longitude (ex: -46.6333)"
                    id="longitude"
                    type="number"
                    step="any"
                    value={longitude ?? ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      setLongitude(value ? parseFloat(value) : null);
                    }}
                  />
                </Input.Root>
              </div>
              <div className="w-full">
                <Map
                  center={mapCenter}
                  zoom={latitude && longitude ? 15 : 13}
                  markers={
                    latitude && longitude
                      ? [
                          {
                            position: [latitude, longitude],
                            title: title || "Localização do evento",
                          },
                        ]
                      : []
                  }
                  onMapClick={handleMapClick}
                  editable={true}
                  interactive={true}
                  height="400px"
                />
              </div>
            </div>
          </div>

          <div className="grid gap-3 lg:grid-cols-form">
            <label
              htmlFor="banner"
              className="text-sm font-medium text-zinc-700 dark:text-zinc-100"
            >
              Banner do evento (530x300)
            </label>
            <div className="flex gap-3">
              <Input.Root>
                <Input.Prefix>
                  <Image className="h-5 w-5 text-zinc-500" />
                </Input.Prefix>
                <Input.Control
                  name="banner"
                  id="banner"
                  type="file"
                  onChange={handleBannerSelected}
                />
              </Input.Root>
            </div>
          </div>
          <div className="grid gap-3 lg:grid-cols-form">
            <label
              htmlFor="banner"
              className="text-sm font-medium text-zinc-700 dark:text-zinc-100"
            >
              Banner do evento site (1680x500)
            </label>
            <div className="flex gap-3">
              <Input.Root>
                <Input.Prefix>
                  <Image className="h-5 w-5 text-zinc-500" />
                </Input.Prefix>
                <Input.Control
                  name="banner"
                  id="banner"
                  type="file"
                  onChange={handleBannerSiteSelected}
                />
              </Input.Root>
            </div>
          </div>

          <div className="pt-5">
            <EventGalleryInput
              onPhotosChange={handleGalleryPhotosChange}
              disabled={loading}
            />
          </div>

          <div className="grid gap-3 lg:grid-cols-form pt-5">
            <label className="text-sm font-medium text-zinc-700 dark:text-zinc-100">
              Instituições
            </label>
            <EventInstitutionsPicker
              allowAll={allowAllInstitutions}
              selectedIds={institutionIds}
              onAllowAllChange={setAllowAllInstitutions}
              onSelectedIdsChange={setInstitutionIds}
            />
          </div>

          <div className="flex items-center justify-end gap-2 pt-5">
            <Button type="button" variant="outline">
              Cancelar
            </Button>
            <Button type="submit" form="users" variant="primary">
              Salvar
            </Button>
          </div>
        </form>
      )}
    </>
  );
}
